import { useState, useEffect } from 'react';
import { db } from '@/lib/supabaseQuery';

export interface Location {
  location_id: number;
  iso_alpha2: string | null;
  name: string;
  short_name: string | null;
  native_slug: string | null;
  native_name: string | null;
  slug: string | null;
  searchableNames?: string;
}

/**
 * Hook to fetch location data directly from the `locations` table.
 * 
 * @description
 * This is the ADMIN/TECHNICAL hook for components that need raw location data
 * with English names and technical identifiers (iso_alpha2, location_id).
 * 
 * **Data Source:** `locations` table (single source of truth for location master data)
 * 
 * **When to use:**
 * - Admin components displaying location lists
 * - Technical components needing iso_alpha2 codes
 * - Any component where English names are acceptable
 * 
 * **When NOT to use:**
 * - User-facing UI that should show localized names → use `useLocalizedLocations`
 * - URL slug generation for routing → use `useTranslatedSlug()`
 * - Map components with coordinates → use `useLocationsWithCoordinates`
 * 
 * @returns {{ locations: Location[], isLoading: boolean }}
 * 
 * @example
 * const { locations, isLoading } = useLocations();
 * // Returns: [{ location_id: "1", iso_alpha2: "US", name: "United States", slug: "united-states", ... }]
 */
export const useLocations = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLocations = async () => {
      setIsLoading(true);
      
      // Query all locations from the locations table
      const data = await db.fetchAll<{
        location_id: number;
        iso_alpha2: string | null;
        name: string;
        short_name: string | null;
        native_name: string | null;
        native_slug: string | null;
        slug: string | null;
      }>('locations', 'location_id, iso_alpha2, name, short_name, native_name, native_slug, slug', {
        orderBy: { column: 'name', ascending: true }
      });
      
      if (data) {
        setLocations(data.map(l => ({
          location_id: l.location_id,
          iso_alpha2: l.iso_alpha2 || null,
          name: l.name || '',
          short_name: l.short_name || null,
          slug: l.slug || null,
          native_slug: l.native_slug || null,
          native_name: l.native_name || null,
        })));
      }
      setIsLoading(false);
    };
    
    loadLocations();
  }, []);

  return { locations, isLoading };
};
